import { FC, useEffect } from "react";
import {
  useGetCompanyListBasicQuery,
  SortDir,
  type OpportunityGeneratorFilter,
  type GetCompanyListBasicQueryVariables,
} from "@/generated/company-search";
import { COMPANY_SEARCH_CLIENT } from "@/utils/consts";
import {
  actionLabelFor,
  bandRange,
  type Band,
  type MatrixThresholds,
} from "@/lib/scoring/matrix/bands";
import { MatrixCell } from "./MatrixCell";

type ScoringProfilesInput =
  GetCompanyListBasicQueryVariables["scoringProfiles"];

interface MatrixCellContainerProps {
  /** Segment filters (already stripped of intent UI-only fields). */
  baseFilters?: OpportunityGeneratorFilter;
  /** The focused profile, transformed for the query (drives fit/intent scores). */
  scoringProfiles: ScoringProfilesInput;
  localizeInstalls: boolean;
  fit: Band;
  intent: Band;
  thresholds: MatrixThresholds;
  onClick: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Fetches one Fit×Intent bucket for the focused profile: count + a few sample
 * company names, via the existing search endpoint with fit+intent range filters.
 * Each cell queries independently so the matrix fills in progressively.
 */
export const MatrixCellContainer: FC<MatrixCellContainerProps> = ({
  baseFilters,
  scoringProfiles,
  localizeInstalls,
  fit,
  intent,
  thresholds,
  onClick,
  onLoadingChange,
}) => {
  const { data, loading } = useGetCompanyListBasicQuery({
    variables: {
      filters: {
        ...(baseFilters ?? {}),
        fitScore: bandRange(fit, thresholds),
        intentScore: bandRange(intent, thresholds),
      },
      localizeInstalls,
      page: 0,
      per: 3,
      sort: [{ field: "revenue_range_min", direction: SortDir.DESC }],
      scoringProfiles,
    },
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  useEffect(() => {
    onLoadingChange?.(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const search = data?.opportunity_generator_search;
  const sampleNames = (search?.data ?? [])
    .map((c) => c?.name ?? "")
    .filter((name): name is string => Boolean(name));

  return (
    <MatrixCell
      loading={loading && !search}
      onClick={onClick}
      cell={{
        fit,
        intent,
        count: search?.count ?? 0,
        sampleNames,
        actionLabel: actionLabelFor(fit, intent),
      }}
    />
  );
};
